import { CreditCard } from "account/_shared/models/CreditCard";
import { API } from "aws-amplify";
import { useUserPanels } from "dashboard/_root/contexts/UserPanelsContext";
import { useEffect, useState } from "react";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";
import { useModal } from "_shared/contexts/ModalContext";
import { useUserSubscriptionsQuery } from "_shared/graphql";

interface Props {
  subId: string;
  onClose: () => void;
}

function ConfirmPurchase({ subId, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [defaultCard, setDefaultCard] = useState<CreditCard | null>(null);

  const { onErrorMsg } = useModal();
  const { fetchPanels } = useUserPanels();
  const { data } = useUserSubscriptionsQuery();
  const selectedSubscription = data?.subscriptions.find(
    (sub) => sub.id === subId,
  )!;
  const isPlanUpgrade = selectedSubscription?.key?.endsWith("plan");

  useEffect(() => {
    const fetchDefaultCard = async () => {
      try {
        const cardsRes: CreditCard[] = await API.post(
          "auth",
          "/cognito/listCards",
          {},
        );
        setDefaultCard(cardsRes.find((card) => card.default)!);
      } catch {
        onErrorMsg(
          "There was an error fetching your payment details.",
          onClose,
        );
      }
    };
    fetchDefaultCard();
  }, []);

  const onConfirm = async () => {
    setLoading(true);
    try {
      // construct price ids to send to backend
      let addedPriceIds: string[] = [];
      let removedPriceIds: string[] = [];
      if (isPlanUpgrade) {
        const removedPlan = data?.user_subscriptions[0]!;
        const addedPriceId = data?.subscriptions.find((sub) => sub.id === subId)
          ?.monthly_daily_price_id!;
        addedPriceIds = [addedPriceId];
        removedPriceIds = [removedPlan?.price_id!];
      } else {
        addedPriceIds = [selectedSubscription?.monthly_daily_price_id!];
      }
      // send to backend
      const res = await API.post("auth", "/cognito/changeSubscription", {
        body: {
          addedPriceIds,
          removedPriceIds,
        },
      });
      if (res.code === "Error") {
        throw new Error(res.message);
      }
      // fetch panels to update addons
      await fetchPanels();
      setPaymentConfirmed(true);
    } catch (err) {
      console.log(err);
      onErrorMsg(
        "There was an error confirming your payment. Please try again.",
      );
    }
    setLoading(false);
  };

  const titleContent = paymentConfirmed
    ? "Congratulations!"
    : "Payment Details";
  const subtitleContent = paymentConfirmed
    ? `You may now view the ${selectedSubscription.name} Dashboard`
    : `You are about to upgrade to the ${
        selectedSubscription.name
      } Dashboard. You will be charged $${
        selectedSubscription[`monthly_daily_price`]
      } today using the card ending in ${defaultCard?.last4 || "-"}.`;

  return (
    <CustomModal
      open={true}
      onClose={onClose}
      title={titleContent}
      subtitle={subtitleContent}
    >
      <i
        className={
          paymentConfirmed
            ? "justify-center fas fa-check-circle"
            : "justify-center fas fa-wallet"
        }
        style={{ color: "#75ce41", fontSize: 50, margin: "20px auto" }}
      />
      <div className="footer-buttons">
        <Button onClick={onConfirm} loading={loading} fullWidth>
          {paymentConfirmed ? "Close" : "Confirm"}
        </Button>
      </div>
    </CustomModal>
  );
}

export default ConfirmPurchase;
